import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private addRefreshSubscriber(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private forceLogout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
    toast.error('Session expired. Please login again.');
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response) {
          const { status, data } = error.response;
          const originalRequest = error.config as RetryableRequestConfig | undefined;

          switch (status) {
            case 400: {
              const badReqData = data as { message?: string; error?: { message: string } };
              toast.error(badReqData.message || badReqData.error?.message || 'Invalid request.');
              break;
            }
            case 401: {
              const isLoginRequest = originalRequest?.url?.includes('/auth/login');
              const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');

              if (isLoginRequest || isRefreshRequest) break;

              const refreshToken = localStorage.getItem('refresh_token');
              if (!refreshToken || originalRequest?._retry) {
                this.forceLogout();
                break;
              }

              if (originalRequest) {
                originalRequest._retry = true;

                if (this.isRefreshing) {
                  // Another refresh is in progress -- queue this request
                  return new Promise((resolve) => {
                    this.addRefreshSubscriber((newToken: string) => {
                      originalRequest.headers.Authorization = `Bearer ${newToken}`;
                      resolve(this.client.request(originalRequest));
                    });
                  });
                }

                this.isRefreshing = true;

                try {
                  const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                    refresh_token: refreshToken,
                  });
                  const newAccessToken = res.data?.data?.access_token;
                  const newRefreshToken = res.data?.data?.refresh_token;

                  if (newAccessToken) {
                    localStorage.setItem('access_token', newAccessToken);
                    if (newRefreshToken) {
                      localStorage.setItem('refresh_token', newRefreshToken);
                    }
                    this.onTokenRefreshed(newAccessToken);
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return this.client.request(originalRequest);
                  }
                  this.forceLogout();
                } catch {
                  this.forceLogout();
                } finally {
                  this.isRefreshing = false;
                }
              } else {
                this.forceLogout();
              }
              break;
            }
            case 403:
              toast.error('You do not have permission to perform this action.');
              break;
            case 404:
              toast.error('Resource not found.');
              break;
            case 422: {
              const errorData = data as { message?: string; error?: { message: string } };
              toast.error(errorData.message || errorData.error?.message || 'Validation error.');
              break;
            }
            case 500:
              toast.error('Server error. Please try again later.');
              break;
            default:
              const defaultData = data as { message?: string; error?: { message: string } };
              toast.error(defaultData.message || defaultData.error?.message || 'An error occurred.');
          }
        } else if (error.request) {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error('An unexpected error occurred.');
        }

        return Promise.reject(error);
      }
    );
  }

  public getInstance(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient().getInstance();
export default apiClient;
